import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useGetNotificationsListQuery, useMarkNotificationAsReadMutation } from '@/store/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils';
import { SocketContext } from '../../contexts/socket.context';
import { NotificationChannel, ON_NOTIFICATIONS } from '@sportspredict/shared';
import { ROUTES } from '@/constants';

export default function NotificationPopover({ setNotificationsReady, user, triggerClassName }) {
	const socket = useContext(SocketContext);
	const [unreadCount, setUnreadCount] = useState(0);
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const triggerPointerActivatedRef = useRef(false);

	const [markNotificationAsRead] = useMarkNotificationAsReadMutation();

	const { email: userEmail, id: userId } = user || {};

	const {
		data: fetchedNotifications = [],
		isLoading,
		refetch,
	} = useGetNotificationsListQuery(undefined, { skip: !userId });

	const openRef = useRef(open);
	useEffect(() => {
		openRef.current = open;
	}, [open]);

	useEffect(() => {
		if (open) refetch();
	}, [open]);

	useEffect(() => {
		if (!userEmail) {
			setUnreadCount(0);
			return;
		}
		const unread = fetchedNotifications.filter((n) => !n.is_read?.[userEmail]).length;
		setUnreadCount(unread);
	}, [fetchedNotifications, userEmail]);

	const notificationsEventListener = useCallback(
		(data) => {
			if (!data || !Array.isArray(data.targetUsers) || !data.targetUsers.includes(userEmail))
				return;
			if (data.channel === NotificationChannel.AppToast) return;

			if (openRef.current) {
				refetch();
			} else {
				setUnreadCount((prev) => prev + 1);
			}
		},
		[userEmail, refetch]
	);

	useEffect(() => {
		if (!socket || !userId) return;

		socket.on(ON_NOTIFICATIONS, notificationsEventListener);
		setNotificationsReady(true);

		return () => {
			socket.off(ON_NOTIFICATIONS, notificationsEventListener);
		};
	}, [socket, userId, notificationsEventListener]);

	const getTimePassed = (dateString) => {
		if (!dateString) return '';
		const created = new Date(dateString);
		const now = new Date();
		const diffMs = now - created;

		const mins = Math.floor(diffMs / (1000 * 60));
		if (mins < 60) return `${mins}m`;

		const hrs = Math.floor(mins / 60);
		if (hrs < 24) return `${hrs}h`;

		const days = Math.floor(hrs / 24);
		return `${days}d`;
	};

	const markAsRead = async (notificationId) => {
		if (!user) return;

		try {
			await markNotificationAsRead({
				id: notificationId,
				is_read: true,
				email: user.email,
			}).unwrap();
			setUnreadCount((prev) => Math.max(0, prev - 1));
		} catch (error) {
			console.error(error);
			throw new Error('Error marking notification as read');
		}
	};

	const markAllAsRead = async () => {
		if (!user || fetchedNotifications.length === 0) return;

		try {
			const unreadNotifications = fetchedNotifications.filter(
				(n) => !(n.is_read && n.is_read[user.email])
			);

			await Promise.all(
				unreadNotifications.map((notification) =>
					markNotificationAsRead({
						id: notification.id,
						is_read: true,
						email: user.email,
					}).unwrap()
				)
			);
		} catch (error) {
			console.error(error);
			throw new Error('Error marking all notifications as read');
		}
	};

	const handleOpenChange = (next) => {
		const shouldMarkAsRead = openRef.current && !next;
		setOpen(next);
		openRef.current = next;

		if (shouldMarkAsRead) {
			void markAllAsRead().catch(console.error);
		}
	};

	const handleTriggerPointerDown = (event) => {
		if (!event.isPrimary || event.pointerType === 'mouse') return;

		triggerPointerActivatedRef.current = true;
		handleOpenChange(!openRef.current);
	};

	const handleTriggerClick = (event) => {
		if (!triggerPointerActivatedRef.current) return;

		triggerPointerActivatedRef.current = false;
		event.preventDefault();
	};

	const handleNotificationClick = async ({ id, event_id, lobby_id }) => {
		await markAsRead(id);

		const url =
			event_id && lobby_id
				? `${ROUTES.LOBBY_DETAILS}?id=${lobby_id}&eventId=${event_id}`
				: event_id
					? `${ROUTES.EVENT_DETAILS}?id=${event_id}`
					: null;

		if (url) {
			navigate(url);
		}
	};

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					onPointerDown={handleTriggerPointerDown}
					onClick={handleTriggerClick}
					aria-label={
						unreadCount > 0 ? 'Notifications, unread notifications available' : 'Notifications'
					}
					variant="ghost"
					size="icon"
					className={cn(
						'relative w-[48px] h-[48px] bg-[#1C2D4A] text-white p-2 rounded-full',
						triggerClassName
					)}
				>
					<Bell className="h-6 w-6" />
					{unreadCount > 0 && (
						<span
							className="absolute right-0 top-0 size-2.5 rounded-full bg-blue-custom-2 ring-2 ring-white"
							aria-hidden="true"
						/>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[296px] p-0 bg-[#171E36] border border-[#2D3A6C] rounded-tl rounded-tr shadow-lg max-h-[70vh] flex flex-col"
				align="end"
				sideOffset={8}
			>
				<div className="px-3 py-1 border-b border-white/10 flex items-center justify-between">
					<h3 className="text-sm font-bold text-white">Notifications</h3>
				</div>

				<div className="overflow-y-auto grow">
					{isLoading ? (
						<div className="flex items-center justify-center p-8">
							<div className="w-6 h-6 border-2 border-white/10 border-t-blue-custom-2 rounded-full animate-spin" />
						</div>
					) : fetchedNotifications.length === 0 ? (
						<div className="p-8 text-center text-gray-400">
							<Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
							<p>No notifications</p>
						</div>
					) : (
						<AnimatePresence>
							{fetchedNotifications.map((notification) => {
								const isRead = notification.is_read && notification.is_read[user?.email];

								return (
									<motion.div
										key={notification.id}
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, height: 0 }}
										className={cn(
											'border-b border-white/5 last:border-0 cursor-pointer transition-colors',
											isRead
												? 'bg-[#101423] hover:bg-[#101423]/50'
												: 'bg-[#1E2748] hover:bg-[#1E2748]/50'
										)}
										onClick={() => handleNotificationClick(notification)}
									>
										<div className="p-2 flex gap-3">
											<div className="flex-1 min-w-0">
												<h4 className="font-normal text-sm text-[#949599]">{notification.title}</h4>
												<p
													className={cn(
														'text-sm font-normal line-clamp-3 wrap-break-word',
														isRead ? 'text-[#D4D5D8]' : 'text-[#F7F7F8]'
													)}
												>
													{notification.message}
												</p>
												<p className="text-[#81A1F8] text-xs font-medium">
													{getTimePassed(notification.created_date)}
												</p>
											</div>

											{!isRead && (
												<span
													className="self-start mt-1 mr-1 w-1.5 h-1.5 rounded-full bg-blue-custom-2 shrink-0"
													aria-hidden
												/>
											)}
										</div>
									</motion.div>
								);
							})}
						</AnimatePresence>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
